import React, { useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useAuthContext } from '../../context/auth.context'
import { useApiFormOrganizationDeletion } from '../../api-new/state/content'
import { useApiListAuthOrganization } from '../../api-new/state/auth'
import { serverMessage } from '../error/server-message'
import { SCHEDULED_PATH, deletionDay, isRestrictedSession, readPendingOnce } from '../organization-deletion/organization-deletion'
import { ErrorReference } from '../error/error-reference'
import { useErrorLine } from '../error/use-error-line'

// "1 organization is scheduled for deletion" (delete organization, spec
// §2.5, plan Task 27): an owner signed in to a live organization sees the
// organizations they can still restore (GET organization-deletion/pending:
// only their own, only as an owner; a system admin or an impersonated
// session gets none). Read once per session (the login): a shared cache
// survives the banner's remounts -- e.g. the full layout coming back -- and
// a restore forgets it (readPendingOnce / markRestored).
//
// Restore switches into that organization (ruling R7): the admin
// organization list carries it for its owners (scheduledForDeletion), and
// the switch gives the restricted restore session, which opens the restore
// page.

const countLine = n => (n === 1 ? '1 organization is scheduled for deletion' : `${n} organizations are scheduled for deletion`)

export const ScheduledDeletionBanner = () => {
  const authContext = useAuthContext()
  const history = useHistory()

  const state = authContext.state || {}
  const restricted = isRestrictedSession(state.jwtClaims)
  const sessionKey = !restricted && state.loginID ? String(state.loginID) : null

  const apiFormOrganizationDeletion = useApiFormOrganizationDeletion()
  const deletionRef = useRef(apiFormOrganizationDeletion)
  deletionRef.current = apiFormOrganizationDeletion

  const apiListAuthOrganization = useApiListAuthOrganization()
  const organizationsRef = useRef(apiListAuthOrganization)
  organizationsRef.current = apiListAuthOrganization

  const [pending, setPending] = useState([])
  const [opening, setOpening] = useState(null)
  const [error, failure, setError] = useErrorLine()

  useEffect(() => {
    if (!sessionKey) return
    let live = true
    // An older content service, or any failure: no banner.
    readPendingOnce(sessionKey, () => deletionRef.current.get('pending')).then(items => live && setPending(items))
    return () => {
      live = false
    }
  }, [sessionKey])

  const handleRestore = async item => {
    setError(null)
    setOpening(item.organizationID)
    try {
      const refreshToken = authContext.state.refreshToken
      const organizations = (await organizationsRef.current.get(refreshToken)) || []
      const target = organizations.find(o => o.organizationID === item.organizationID)
      if (!target) throw new Error('not in the organization list')
      const authToken = await organizationsRef.current.put(refreshToken, { ...target, chosen: true })
      authContext.setAuthToken(authToken)
      history.push(SCHEDULED_PATH)
    } catch (err) {
      setOpening(null)
      setError(serverMessage(err, `We couldn't open ${item.organizationName}. Try again, or choose it from Change Organization.`), err)
    }
  }

  if (!sessionKey || pending.length === 0) return null

  return (
    <div className="alert alert-warning rounded-0 m-0 py-2 px-3" role="region" aria-label="Organizations scheduled for deletion">
      <div className="fw-semibold">{countLine(pending.length)}</div>
      <ul className="list-unstyled m-0">
        {pending.map(item =>
          <li key={item.organizationID} className="d-flex flex-wrap align-items-center gap-2">
            <span>{`${item.organizationName} is permanently deleted on ${deletionDay(item.purgeAfter)}.`}</span>
            <button type="button" className="btn btn-sm btn-outline-dark py-0" disabled={opening !== null} onClick={() => handleRestore(item)}>
              Restore
            </button>
          </li>
        )}
      </ul>
      {error && <div className="small text-danger mt-1" role="alert">{error}</div>}
      {error && <ErrorReference error={failure} className="mt-1" />}
    </div>
  )
}
